import { ProjectErrorComponent } from "@/components/error";
import { PendingProjectComponent } from "@/components/pending";
import { trpc } from "@/lib/trpc";
import { projects } from "@papier/shared";
import { Button } from "@papier/ui/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@papier/ui/components/ui/card";
import { Separator } from "@papier/ui/components/ui/separator";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, linkOptions, Outlet } from "@tanstack/react-router";
import { formatDistanceToNowStrict } from "date-fns";
import { ArrowLeft, Calendar, FileText, LayoutDashboard, Settings, User } from "lucide-react";
import { z } from "zod";

export const Route = createFileRoute("/_app/projects/$projectSlug/_layout")({
  component: ProjectLayout,
  params: {
    parse: (params) => {
      return z.object({ projectSlug: projects.shape.slug }).parse(params);
    },
  },
  loader: async ({ context: { queryClient, trpc }, params }) => {
    queryClient.ensureQueryData(trpc.projects.getBySlug.queryOptions(params));
  },
  wrapInSuspense: true,
  pendingComponent: () => <PendingProjectComponent />,
  errorComponent: ({ error }) => <ProjectErrorComponent error={error} />,
  head: () => ({
    meta: [{ title: "Projects" }],
  }),
});

const navigationItems = linkOptions([
  {
    title: "Templates",
    icon: LayoutDashboard,
    to: "/projects/$projectSlug",
    from: "/projects/$projectSlug",
  },
  {
    title: "Settings",
    icon: Settings,
    to: "/projects/$projectSlug/settings",
    from: "/projects/$projectSlug",
  },
]);

function ProjectLayout() {
  const { useParams } = Route;
  const params = useParams();

  const { data: project } = useSuspenseQuery(trpc.projects.getBySlug.queryOptions(params));

  return (
    <main className="grid gap-6">
      <div>
        <Button variant="ghost" className="glass-button cursor-default" asChild>
          <Link to="/projects">
            <ArrowLeft />
            Back to Projects
          </Link>
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <figure
            className="grid size-16 shrink-0 select-none place-items-center rounded-full text-xl font-semibold text-white"
            style={{ backgroundColor: project.color }}
          >
            {project.name.charAt(0).toUpperCase()}
          </figure>
          <div>
            <h1 className="text-foreground text-3xl font-bold">{project.name}</h1>
            <p className="text-muted-foreground mt-1">{project.description}</p>
            <div className="mt-2 flex items-center gap-4">
              <div className="text-muted-foreground flex items-center gap-1 text-sm">
                <Calendar className="size-4" />
                {formatDistanceToNowStrict(project.createdAt, { addSuffix: true })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-7 xl:grid-cols-5">
        {/* Sidebar */}
        <Card className="glass-card h-max gap-4 lg:col-span-2 xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="size-5" />
              Created By
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-foreground text-sm font-medium">
              {project.createdBy?.name || "Unknown"}
            </div>
            <div className="text-muted-foreground text-xs">
              {project.createdBy?.email || "Unknown"}
            </div>
          </CardContent>
          <Separator />
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="size-5" />
              Project Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-left">
              <div className="text-foreground text-2xl font-semibold">
                {project.templatePublishedCount}
              </div>
              <div className="text-muted-foreground text-sm">Published Templates</div>
            </div>
          </CardContent>
          <Separator />
          <CardHeader>
            <CardTitle>Navigation</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2">
            {navigationItems.map((item) => (
              <Button
                asChild
                key={item.title}
                variant="ghost"
                className="dark:hover:bg-primary/10 hover:bg-primary/10 w-full justify-start"
              >
                <Link
                  from={item.from}
                  to={item.to}
                  activeProps={{ className: "bg-primary/20 text-foreground" }}
                  activeOptions={{ exact: true, includeSearch: false }}
                >
                  <item.icon className="size-5" />
                  <span>{item.title}</span>
                </Link>
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="space-y-6 lg:col-span-5 xl:col-span-4">
          <Outlet />
        </div>
      </div>
    </main>
  );
}
